import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-md mx-auto px-6 py-16 text-center space-y-4" data-testid="reset-no-token">
          <div className="w-14 h-14 rounded-full bg-destructive/15 grid place-items-center mx-auto">
            <AlertCircle className="w-6 h-6 text-destructive"/>
          </div>
          <h1 className="font-display text-3xl">{lang === "it" ? "Link non valido" : "Invalid link"}</h1>
          <p className="text-sm text-muted-foreground">
            {lang === "it"
              ? "Il link di reset non è valido o è incompleto. Richiedi un nuovo link."
              : "The reset link is invalid or incomplete. Please request a new one."}
          </p>
          <Link to="/forgot-password">
            <Button className="lift">{lang === "it" ? "Richiedi nuovo link" : "Request new link"}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error(lang === "it" ? "La password deve avere almeno 6 caratteri" : "Password must be at least 6 characters");
      return;
    }
    if (pw !== confirm) {
      toast.error(lang === "it" ? "Le password non coincidono" : "Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success(lang === "it" ? "Password aggiornata" : "Password updated");
      setTimeout(() => nav("/auth?mode=login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16" data-testid="reset-password-page">
        {done ? (
          <div className="text-center space-y-4" data-testid="reset-done-state">
            <div className="w-14 h-14 rounded-full bg-sage/15 grid place-items-center mx-auto">
              <CheckCircle2 className="w-6 h-6 text-sage"/>
            </div>
            <h1 className="font-display text-4xl tracking-tight">
              {lang === "it" ? "Password aggiornata" : "Password updated"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {lang === "it" ? "Ti stiamo reindirizzando al login…" : "Redirecting to login…"}
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-4xl tracking-tight mb-2" data-testid="reset-title">
              {lang === "it" ? "Nuova password" : "New password"}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {lang === "it" ? "Scegli una nuova password sicura per il tuo account." : "Choose a new secure password for your account."}
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <Label htmlFor="pw">{lang === "it" ? "Nuova password" : "New password"}</Label>
                <Input id="pw" type="password" required minLength={6}
                       value={pw} onChange={(e) => setPw(e.target.value)}
                       data-testid="reset-input-password"/>
              </div>
              <div>
                <Label htmlFor="confirm">{lang === "it" ? "Conferma password" : "Confirm password"}</Label>
                <Input id="confirm" type="password" required minLength={6}
                       value={confirm} onChange={(e) => setConfirm(e.target.value)}
                       data-testid="reset-input-confirm"/>
              </div>
              <Button type="submit" disabled={loading}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift"
                      data-testid="reset-submit">
                <KeyRound className="w-4 h-4 mr-1"/>
                {loading ? "…" : (lang === "it" ? "Reimposta password" : "Reset password")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
